/**
 * Created by james on 4/30/14.
 */
"use strict";

// ---------------------------------------------------
// Load Super PseudoClass Module
var SoaModel = require('bunsen/models/SoaModel');
var SoaCollection = require('bunsen/collections/SoaCollection');
var endpoints = require('dibs-endpoints');

// ---------------------------------------------------
// Define Module

/**
 * @class ShipmentQuotes
 * @extends SoaCollection
 */
var ShipmentQuotes = module.exports = SoaCollection.extend();

// ---------------------------------------------------
// Dependencies

var ShipmentQuote = require('bunsen/models/logistics/ShipmentQuote');
var _ = require('underscore');
var ServiceMethod = require('bunsen/models/logistics/ServiceMethod');

// ---------------------------------------------------
// Local vars

var regionIdField = 'serviceMethod.region.id';
var serviceMethodTypeField = 'serviceMethod.serviceMethodType';
var regionCodeField = 'serviceMethod.region.code';

// ---------------------------------------------------
// Define Module Instance Properties

_.extend(ShipmentQuotes.prototype, {

    model: ShipmentQuote,

    /**
     * Find the first ShipmentQuote with a
     * service method type for local pickup
     * @returns {ShipmentQuote}
     */
    findLocalPickupQuote: function () {
        return this._whereFieldValsIn([ServiceMethod.METHOD_LOCAL_PICKUP], serviceMethodTypeField, true);
    },

    /**
     * @returns {boolean}
     */
    hasLocalPickupQuote: function () {
        return !!this.findLocalPickupQuote();
    },

    /**
     * Returns an array of shipment quotes form
     * the collection instance with the local
     * pickup quote omitted
     * @returns {ShipmentQuote[]}
     */
    noLocalPickupQuotes: function () {
        return _.reject(this.models, function (model) {
            return model.isLocalPickup();
        });
    },

    /**
     * @returns {boolean}
     */
    hasFreeQuote: function () {
        return _.some(this.models, function (model) {
            return model.isComplimentaryShipping();
        });
    },

    /**
     * @returns {boolean}
     */
    isComplementaryEverywhere: function () {
        var hasAllFreeQuotes;
        var coversAllRegions;

        hasAllFreeQuotes = _.every(this.models, function (model) {
            if (model.regionIsWorld()) {
                coversAllRegions = true;
            }
            return model.isComplimentaryShipping();
        });

        return hasAllFreeQuotes && coversAllRegions;
    },

    /**
     * @returns {boolean}
     */
    hasOnlyGlobal: function () {
        return _.every(this.models, function (model) {
            return model.get('serviceMethod.region.code') === 'REST_OF_WORLD';
        });
    },

    /**
     * Given a serviceMethodType and a serviceType, find
     * the first shipment quote that matches both
     * @param {string} serviceMethodType
     * @param {string} serviceType
     * @returns {ShipmentQuote}
     */
    findQuoteFromMethodAttrs: function (serviceMethodType, serviceType) {
        return this.find(function (quote) {
            var serviceMethod = quote.get('serviceMethod');
            var sameMethodType;
            var sameServiceType;
            if (serviceMethod) {
                sameMethodType = serviceMethod.get('serviceMethodType') === serviceMethodType;
                sameServiceType = serviceMethod.get('serviceType') === serviceType;
            }
            return serviceMethod && sameMethodType && sameServiceType;
        });
    },

    /**
     * Find a region by code
     * @returns {ShipmentQuote}
     */
    findRegionByCode: function (code) {
        return this._whereFieldValsIn([code], regionCodeField, true);
    },

    /**
     * Find the all regions for a given id
     * Assumes serviceMethod is inflated
     * @param id
     * @returns {ShipmentQuote[]}
     */
    whereRegion: function (id) {
        return this._whereFieldValsIn([id], regionIdField, false);
    },
    /**
     * Find the first region that matches
     * an id
     * Assumes serviceMethod is inflated.
     * @param id
     * @returns {ShipmentQuote}
     */
    findWhereRegion: function (id) {
        return this._whereFieldValsIn([id], regionIdField, true);
    },
    /**
     * Find all shipment quotes a list of ids.
     * Assumes serviceMethod is inflated.
     * @param {string[]|number[]} ids
     * @returns {ShipmentQuote[]}
     */
    whereRegionIn: function (ids) {
        return this._whereFieldValsIn(ids, regionIdField, false);
    },
    /**
     * Find the first shipment quote
     * that has an id in the passed
     * array of ids.
     * Assumes serviceMethod is inflated.
     * @param {string[]|number[]} ids
     * @returns {ShipmentQuote}
     */
    findWhereRegionIn: function (ids) {
        return this._whereFieldValsIn(ids, regionIdField, true);
    },
    /**
     * Find the first models for each region in
     * the collection.
     * @returns {ShipmentQuote[]}
     */
    firstModelsForRegions: function () {
        var regIds = [];
        return this.reject(function (model) {
            var regId = model.get(regionIdField);
            if (_.indexOf(regIds, regId) > -1) {
                return true;
            }
            regIds.push(regId);
            return false;
        });
    },
    /**
     * Helper method to find models based
     * on a field (supports relational fields)
     * that have a value contained in a list
     * of passed values
     * @param {Array} vals The values to match
     * @param {string} field The field to find a match for
     * @param {boolean} returnFirstMatch
     *      Should return the first match and stop the search
     * @returns {ShipmentQuote|ShipmentQuotes[]|undefined}
     * @private
     */
    _whereFieldValsIn: function (vals, field, returnFirstMatch) {
        var matches = [];
        var i = 0;
        var fieldVal;
        var model;
        var len = this.models.length;
        for (i; i < len; i++) {
            model = this.at(i);
            fieldVal = model.get(field);
            if (_.indexOf(vals, fieldVal) > -1) {
                if (returnFirstMatch) {
                    return model;
                }
                matches.push(model);
            }
        }
        return returnFirstMatch ? undefined : matches;
    },

    /**
     * Checks the shipmentQuotes collection for fields validation
     * with logistics service
     * @returns {Object}
     */
    validateAgainstServices: function () {
        var self = this;
        var shipmentQuotesModel = new SoaModel({
            dataList: self
        }, {
            url : function () {
                return endpoints.shipmentQuotes() + '/validate';
            }
        });
        return shipmentQuotesModel.save();
    },
    /**
     * Fetches available shipping options for the passed address and item
     * @param address an address model
     * @param item an item model
     */
    fetchAvailableOptions: function (item, address, currencyConversionRateEventId) {
        var addressFields = ['zipCode', 'city', 'state', 'country'];
        var data = {};

        if (address) {
            data = _.reduce(addressFields, function (data, field) {
                var value = address.get(field);
                if (value) {
                    data['address.' + field] = value;
                }
                return data;
            }, {});
        }

        if (currencyConversionRateEventId) {
            data.currencyConversionRateEventId = currencyConversionRateEventId;
        }

        if (item.id) {
            data.itemId = item.id;
        }

        return this.fetch({
            data: data,
            url: endpoints.availableOptions()
        });
    },
    getLocalShipmentQuotes: function () {
        return this.filter(function (quote) {
            return quote.isLocalPickup();
        });
    },
    getNonLocalShipmentQuotes: function () {
        return this.reject(function (quote) {
            return quote.isLocalPickup();
        });
    },

    hasMultipleNonLocalShipmentQuotes: function () {
        var quotes = this.getNonLocalShipmentQuotes();
        return quotes && quotes.length > 1;
    },

    /**
     * Returns currency of first valid(one which has the currency) shipment
     * quotes (e.g. USD, GBP, EUR).
     * @returns {string}
     */
    getFirstValidQuoteCurrency: function () {
        var i = 0;
        var val;
        for (i; i < this.models.length; i++) {
            val = this.at(i).getFirstCostCurrency();
            if (val) {
                return val;
            }
        }
        return null;
    }

});



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/collections/logistics/ShipmentQuotes.js
// module id = 319
// module chunks = 64